import { springboardAPI } from "src/boot/axios"
import { SpringboardURL } from "src/util/enums/urls"
import { concatUrl, getCustomConfig, getNewAbortHandler } from "src/util/http/httpHelper"
import { ObjectList, PayloadEntity } from "src/util/types/dataModels/genericObject"
import { HttpConfig } from "src/util/types/dataModels/http"
import {
  PERMISSION_ACCESS_CREATE,
  PERMISSION_ACCESS_READ,
  PERMISSION_ACCESS_UPDATE,
  PERMISSION_WORK_DOMAINS,
} from "src/util/types/dataModels/user"
import { WorkDomainFilterParams, WorkDomainGlobal } from "src/util/types/dataModels/workDomain"

/**
 * Return the list of all the Work Domains
 *
 * @param config custom configurations and filtering params
 */
export async function getWorkDomainsGlobalList(
  config: HttpConfig<WorkDomainFilterParams> = {}
): Promise<ObjectList<WorkDomainGlobal>> {
  const entityURL = SpringboardURL.WORK_DOMAINS
  const url = concatUrl(entityURL, { ...config?.mainParams, ...config?.filterParams })

  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", entityURL)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_DOMAINS,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Create a Work Domain
 *
 * @param config custom configurations and filtering params
 * @param workDomain
 */
export async function postWorkDomainGlobal(
  config: HttpConfig = {},
  workDomain: PayloadEntity<WorkDomainGlobal>
): Promise<WorkDomainGlobal> {
  const url = SpringboardURL.WORK_DOMAINS
  const abortHandler = config?.abortHandler || getNewAbortHandler("POST", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_DOMAINS,
    PERMISSION_ACCESS_CREATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.post(url, workDomain, { signal, customConfig })

  return data
}

/**
 * Return a Work Domain
 *
 * @param config custom configurations and filtering params
 * @param id
 */
export async function getWorkDomainGlobal(
  config: HttpConfig = {},
  id: string
): Promise<WorkDomainGlobal> {
  const url = SpringboardURL.WORK_DOMAINS_ID.replace("{id}", id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("GET", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_DOMAINS,
    PERMISSION_ACCESS_READ
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.get(url, { signal, customConfig })

  return data
}

/**
 * Replace an existing Work Domain
 *
 * @param config custom configurations and filtering params
 * @param id
 * @param workDomain
 */
export async function putWorkDomainGlobal(
  config: HttpConfig = {},
  id: string,
  workDomain: PayloadEntity<WorkDomainGlobal>
): Promise<WorkDomainGlobal> {
  const url = SpringboardURL.WORK_DOMAINS_ID.replace("{id}", id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PUT", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_DOMAINS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.put(url, workDomain, { signal, customConfig })

  return data
}

/**
 * Partially update a Work Domain
 *
 * @param config custom configurations and filtering params
 * @param id
 * @param workDomain
 */
export async function patchWorkDomainGlobal(
  config: HttpConfig = {},
  id: string,
  workDomain: Partial<PayloadEntity<WorkDomainGlobal>>
): Promise<WorkDomainGlobal> {
  const url = SpringboardURL.WORK_DOMAINS_ID.replace("{id}", id)
  const abortHandler = config?.abortHandler || getNewAbortHandler("PATCH", url)
  const customConfig = getCustomConfig(
    abortHandler,
    PERMISSION_WORK_DOMAINS,
    PERMISSION_ACCESS_UPDATE
  )
  const signal = abortHandler.controller.signal

  const { data } = await springboardAPI.patch(url, workDomain, { signal, customConfig })

  return data
}
